import {
  Button,
  Checkbox,
  Fieldset,
  TextInput,
} from "@datapay-by-datacom/design-system";
import { useState } from "react";
import type { TriggerRunFormData } from "../../hooks/triggerRunFormUtils";
import styles from "./AdvancedOptionsFieldset.module.css";

type FieldReturn = {
  value: TriggerRunFormData;
  onChange: (value: TriggerRunFormData) => void;
};

type InputFn = (
  key: keyof TriggerRunFormData,
) => { value: string; onChange: (value: string) => void };

export interface AdvancedOptionsFieldsetProps {
  field: FieldReturn;
  input: InputFn;
  isEnterprise: boolean;
}

export function AdvancedOptionsFieldset({
  field,
  input,
  isEnterprise,
}: AdvancedOptionsFieldsetProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className={styles.container}>
      <Button
        variant="tertiary"
        onPress={() => setIsExpanded((prev) => !prev)}
      >
        Advanced Options {isExpanded ? "\u25B4" : "\u25BE"}
      </Button>

      {isExpanded && (
        <Fieldset>
          {isEnterprise && (
            <>
              <TextInput
                label="Shard Size"
                type="number"
                description="Number of packets per shard"
                {...input("shardSize")}
              />

              <TextInput
                label="Max Parallel Shards"
                type="number"
                description="Maximum number of shards to process concurrently"
                {...input("maxParallelShards")}
              />
            </>
          )}

          <TextInput
            label="Timeout Minutes"
            type="number"
            description="Maximum time in minutes before the run times out"
            {...input("timeoutMinutes")}
          />

          <TextInput
            label="Circuit Breaker Threshold"
            type="number"
            description="Error rate threshold (0-1) to trip the circuit breaker"
            {...input("circuitBreakerThreshold")}
          />

          <TextInput
            label="Circuit Breaker Sample Size"
            type="number"
            description="Number of packets to evaluate before checking the threshold"
            {...input("circuitBreakerSampleSize")}
          />

          <Checkbox
            isSelected={field.value.aiAnalysisEnabled}
            onChange={(isChecked) =>
              field.onChange({
                ...field.value,
                aiAnalysisEnabled: isChecked,
              })
            }
          >
            Enable AI Analysis
          </Checkbox>

          {field.value.aiAnalysisEnabled && (
            <TextInput
              label="Custom Analysis Prompt"
              description="Optional prompt to guide AI analysis of variance results"
              {...input("customAnalysisPrompt")}
            />
          )}
        </Fieldset>
      )}
    </div>
  );
}
